import { useEffect, useState } from "react";
import { type FieldPath, type FieldValues, type UseFormProps, type UseFormReturn, useForm, useFormState, useWatch } from "react-hook-form";
import { useDraftStore } from "./store";

export function useFormWithDraft<TFieldValues extends FieldValues = FieldValues, TContext = any>(
  formId: string,
  formOptions?: UseFormProps<TFieldValues, TContext>
): UseFormReturn<TFieldValues, TContext, TFieldValues> & {
  unsavedChanges: boolean;
  isDirty: boolean;
  loading: boolean;
} {
  // Draft store hooks
  const getDraftForm = useDraftStore((state) => state.getForm);
  const setDraftForm = useDraftStore((state) => state.setForm);
  const resetDraftForm = useDraftStore((state) => state.resetForm);

  const form = useForm<TFieldValues, TContext, TFieldValues>(formOptions || {});

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  const allFields = useWatch({ control: form.control });
  const { isDirty, defaultValues } = useFormState({ control: form.control });

  // Auto-save draft on change
  useEffect(() => {
    const current = JSON.stringify(allFields);
    const original = JSON.stringify(defaultValues);
    // Form is reset to default values, clear the draft
    if (current === original && unsavedChanges) {
      resetDraftForm(formId);
      form.reset();
      return;
    }

    if (isDirty) {
      const cleanedValues = Object.fromEntries(Object.entries(allFields).filter(([_, v]) => v !== undefined));
      if (Object.keys(cleanedValues).length > 0) setDraftForm(formId, cleanedValues);
    }
  }, [allFields, isDirty, defaultValues]);

  // Update dirty state UI and flag
  useEffect(() => {
    setUnsavedChanges(isDirty);
  }, [isDirty]);

  // Restore draft on mount
  useEffect(() => {
    const draftData = getDraftForm<TFieldValues>(formId);

    if (draftData) {
      for (const [key, value] of Object.entries(draftData)) form.setValue(key as FieldPath<TFieldValues>, value, { shouldDirty: true });
    }

    setLoading(false);
  }, []);

  return {
    ...form,
    unsavedChanges,
    isDirty,
    loading,
    // Override `handleSubmit` to always process `onInvalid` through a fallback handler
    handleSubmit: (onValid, onInvalid) => form.handleSubmit(onValid, onInvalid),
    // Override `reset` to also clear the draft + UI state
    reset: (values, keepStateOptions) => {
      resetDraftForm(formId);
      setUnsavedChanges(false);
      form.reset(values, keepStateOptions);
    }
  };
}
